// Parses the "SOC Response Actions" free-text section into structured actions.
//
// The section is prose written by several different upstream systems. Observed
// shapes, all of which appear in production:
//
// 1. Checkmark (Use Case templates):
//    Status ✔ "npeck@snpeck.com", had their sessions revoked Success
//           ✔ "npeck@snpeck.com", was disabled on Azure AD Success
//
// 2. Key-value (SentinelOne):
//    Isolate Endpoint: N/A Initiate Full Disk Scan: N/A
//
// 3. Entity + status (XDR / SOAR) — entity is a hostname OR an email:
//    Action Status CN-MJ0LVGPH , no full disk scan command was sent. Not Configured
//    Action Status dkoech@cyflare.com , revoke user's active sessions Not Configured
//                  dkoech@cyflare.com , disable user account Not Configured
//
// 4. Narrative:
//    Endpoint isolated via SentinelOne network quarantine. File quarantined.
//
// Any of them may be wrapped in section prose — a lead-in above and a use-case
// pointer below — which is not an action and must never become one.
//
// DESIGN
//
// Status words are the only reliable structure across these shapes, so they are
// used as DELIMITERS rather than as an afterthought parsed out of each entry.
// Everything between one status and the previous one is a single entry.
//
// And a row is only emitted when the action can be NAMED. The panel renders
// `label` as the action, offers a Request SOC button beside it, and a row whose
// label is a truncated sentence invites the user to ask the SOC to perform a
// fragment of prose. If we cannot say what the action is, we do not claim there
// is one.

import { SocAction } from '../models/ticket.models';

/**
 * What one SOC Response Actions section yielded.
 *
 * `narrative` carries section text that produced no actions, so the panel can
 * show it verbatim. A section that renders empty reads as "the SOC took no
 * action" — a claim about the case rather than about the parser.
 */
export interface SocActionParseResult {
  actions: SocAction[];
  narrative: string | null;
}

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/**
 * Action phrases, MOST SPECIFIC FIRST — the first match wins.
 *
 * Order is load-bearing. "move user to quarantine group" contains "quarantin"
 * and would otherwise be labelled File Quarantine, which names the wrong object
 * and the wrong blast radius: one isolates a file, the other restricts a person.
 */
const ACTION_LABELS: { pattern: RegExp; label: string }[] = [
  /**
   * Derived from the 112 distinct action phrases that appear across 97,024
   * production SOC Response Actions sections, ordered most-specific-first.
   *
   * Order is load-bearing. "move user to quarantine group" (26,260 occurrences)
   * contains "quarantin" and would otherwise read as File Quarantine — one
   * isolates a file, the other restricts a person.
   */

  // Conditional-access group membership. The single most common SOAR action
  // after the big three, and distinct from disabling: the account still exists,
  // it is policy-blocked.
  { pattern: /\b(?:ca policy group|conditionalaccess|soarjail|jailcell|blocked-login-accounts|disabled users|quarantined users|cyflare-jail)\b/i, label: 'CA Policy Group' },

  // Quarantine, split by the object.
  { pattern: /quarantine group|move .*\bto quarantine\b|user .*quarantin/i, label: 'User Quarantine' },
  { pattern: /(?:email|message|mailbox|mail)\b.*quarantin|quarantin.*\b(?:email|message|mailbox)|email .*reject/i, label: 'Email Quarantine' },
  { pattern: /(?:file|threat)\b.*quarantin|quarantin.*\bfile/i, label: 'File Quarantine' },

  // Endpoint containment.
  { pattern: /full disk scan|\bscan\b.*endpoint|endpoint was not scanned/i, label: 'Full Disk Scan' },
  { pattern: /isolat|\bcontain(?:ed|ment)?\b.*endpoint|endpoint .*\bcontain/i, label: 'Network Isolation' },

  // Identity.
  { pattern: /session\w*\s+revo|revo\w*\s+.*session|session revocation/i, label: 'Session Revocation' },
  { pattern: /password reset|reset .*password|reset their password/i, label: 'Password Reset' },
  { pattern: /(?:was|were|been)\s+(?:disabl|deactivat)\w*|(?:disabl|deactivat)\w*\s+(?:on\s+)?(?:azure|entra|active directory)|(?:disabl|deactivat)\w*.*(?:user|account)|(?:user|account).*(?:disabl|deactivat)|disable group/i, label: 'Account Disabled' },
  { pattern: /compromised account playbook/i, label: 'Account Playbook' },
  { pattern: /\bmfa\b|multi.?factor|re.?enrol/i, label: 'MFA Enforcement' },

  // Network / mail blocking.
  { pattern: /blocklist|block.?list|address group|firewall/i, label: 'Firewall Blocklist' },
  { pattern: /\bsender\b.*block|block.*\bsender\b/i, label: 'Sender Block' },
  { pattern: /block\w*.*\b(?:ip|address|domain|url)\b|\b(?:ip|domain)\b.*block\w*/i, label: 'Block' },
  { pattern: /remov\w*.*(?:inbox rule|forwarding|rule|app consent|consent)/i, label: 'Rule Removal' },

  // Generic, last so every specific rule above wins first.
  { pattern: /remediat/i, label: 'Threat Remediation' },
  { pattern: /\bquarantin/i, label: 'Quarantine' },
];

/**
 * Status tokens, used both to classify and to SPLIT the text.
 *
 * Longest-first within the regex alternation so "Not Configured" is never
 * matched as the bare word "Configured", and "Not Taken" never as "Taken".
 */
const STATUS_TOKENS: { text: string; status: SocAction['status']; pattern: string }[] = [
  // Counts are occurrences across 97,024 production sections.
  { text: 'Not Configured', status: 'not_configured', pattern: 'Not\\s+Configured' },   // 105,401
  { text: 'N/A', status: 'not_configured', pattern: 'N\\s*/\\s*A' },                     //  57,415
  { text: 'Success', status: 'taken', pattern: 'Success(?:ful)?' },                   //   8,380
  { text: 'Skipped', status: 'not_taken', pattern: 'Skipped' },                       //   6,385
  { text: 'Exempted', status: 'not_taken', pattern: 'Exempted' },                     //   6,340
  { text: 'Failed', status: 'not_taken', pattern: 'Failed' },                         //   3,531
  // "Blocked" terminates blocklist/sender entries and reports the block LANDED.
  { text: 'Blocked', status: 'taken', pattern: 'Blocked' },                           //   2,211
  { text: 'Unknown', status: 'unknown', pattern: 'Unknown' },                         //      64
  { text: 'Unsuccessful', status: 'not_taken', pattern: 'Unsuccessful' },             //      19
  // Present in the documented formats though rare in the corpus.
  { text: 'Not Applicable', status: 'not_configured', pattern: 'Not\\s+Applicable' },
  { text: 'Not Taken', status: 'not_taken', pattern: 'Not\\s+Taken' },
  { text: 'Completed', status: 'taken', pattern: 'Completed' },
  { text: 'Pending', status: 'not_taken', pattern: 'Pending' },
];

/**
 * One alternation over every status token, used to SPLIT entries.
 *
 * Longest-first so "Not Configured" is never matched as bare "Configured".
 */
const STATUS_RE = new RegExp(
  `\\b(?:${[...STATUS_TOKENS].sort((a, b) => b.pattern.length - a.pattern.length).map((s) => s.pattern).join('|')})\\b`,
  'gi',
);

/**
 * Classify a status, FINDING the token rather than requiring the whole value to
 * be one.
 *
 * Exact matching looked right until measured: a key/value entry's value is
 * everything up to the next key, so "N/A Timeline for additional findings: …"
 * is a perfectly ordinary value and matched nothing. That single mistake put
 * 28,868 rows on screen reading Unknown — the status the reported screenshots
 * were full of.
 *
 * Longest-first, so "Not Configured" is never read as bare "Configured".
 */
function classifyStatus(token: string): { status: SocAction['status']; statusText: string } {
  const normalised = token.replace(/\s+/g, ' ').trim();
  const ordered = [...STATUS_TOKENS].sort((a, b) => b.pattern.length - a.pattern.length);
  for (const t of ordered) {
    if (new RegExp(`\\b(?:${t.pattern})\\b`, 'i').test(normalised)) {
      return { status: t.status, statusText: t.text };
    }
  }
  return { status: 'unknown', statusText: 'Unknown' };
}

/**
 * Sentences that introduce or close the section rather than describe an action.
 *
 * Real tickets wrap the actions in a lead-in ("Actions the SOC performed (or
 * attempted)") and a pointer ("Follow this link for further information on Use
 * Case #3"). The section runs to the next heading the description parser knows,
 * so both land here — and the previous parser turned each into an action row
 * with a Request SOC button offering to action a sentence.
 */
const SECTION_PROSE: RegExp[] = [
  /^actions?\s+(?:that\s+)?(?:the\s+)?soc\s+(?:performed|took|attempted|has)/i,
  /^follow\s+(?:this|the)\s+link/i,
  /^(?:for|see)\s+(?:more|further|additional)?\s*(?:information|details|detail)/i,
  /^see\s+use\s+case/i,
  /^use\s+case\s*#?\d*\s*$/i,
  /^refer\s+to/i,
  /^no\s+(?:soc\s+)?actions?\s+(?:were\s+|was\s+)?(?:taken|performed|required|configured)/i,
  /^this\s+(?:case|ticket|alert)/i,
  // Advisory guidance that trails the actions in some templates. Imperative
  // instructions to the CUSTOMER, not a record of what the SOC did — and
  // "Configure Conditional Access…" was matching as an MFA action 4,240 times.
  /^(?:configure|implement|ensure|enable|determine|review|consider|advise|reach\s+out|verify|validate|please)\b/i,
  /^soc\s+is\s+(?:equipped|able)\b/i,
  /^if\s+this\s+is\b/i,
];

/** Entry markers used across the corpus — see `parseCheckmark`. */
const MARKER = /[✔✓✅☑⚠❌]/;

/**
 * `(?)` opens 9,981 sections and means "outcome unrecorded".
 *
 * Most carry nothing after it, or an explicit nothing ("N/A", "None", "No
 * action taken", "No Response Action Taken"). A few name a real action —
 * "(?) Isolated the endpoint" — so the marker is stripped and whatever follows
 * is parsed normally rather than the section being discarded outright.
 */
const NO_ACTION_SENTINEL =
  /^(?:n\s*\/\s*a|none|no\s+(?:response\s+)?action(?:s)?(?:\s+taken|\s+available)?|nil|-{1,2})\.?$/i;

/** A label the section uses to introduce the entries, not an action itself. */
const ENTRY_MARKERS = /\b(?:Action\s+Status|Status)\s*:?\s*/gi;

/** Hostname (CN-MJ0LVGPH) or email (dkoech@cyflare.com). */
const ENTITY_RE = /^\s*"?([A-Za-z0-9][A-Za-z0-9._%+-]*@[A-Za-z0-9.-]+\.[A-Za-z]{2,}|[A-Z][A-Z0-9-]{2,})"?\s*,\s*/;

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

export function parseSocActions(content: string): SocActionParseResult {
  if (!content || !content.trim()) return { actions: [], narrative: null };

  const original = content.replace(/\s+/g, ' ').trim();

  // `(?)` is an outcome-unknown marker, not content.
  const unmarked = original.replace(/^\(\?\)\s*/, '').trim();
  if (!unmarked || NO_ACTION_SENTINEL.test(unmarked)) return { actions: [], narrative: null };

  const body = stripProse(unmarked);

  // Nothing survived the strip: the section was entirely its own boilerplate,
  // or an explicit "none". Render no block at all rather than echoing it back.
  if (!body || NO_ACTION_SENTINEL.test(body)) return { actions: [], narrative: null };

  /**
   * Residue remains, but the section carries no entry structure and opened as
   * advisory — so it is guidance for the customer, not a record of what the SOC
   * did, all the way down.
   *
   * These blobs ("SOC is equipped to initiate response actions upon customer
   * request. Determine if the activity is authorized… Multi-factor
   * authentication should be…") only LOOK like a lead-in on their first
   * sentence. Stripping sentence-by-sentence left the rest behind, where
   * "Multi-factor authentication" matched an action pattern and became a row
   * with no status.
   *
   * A marker or the "Action Status" introducer is what separates a section that
   * REPORTS actions from one that RECOMMENDS them.
   */
  const hasEntryStructure = MARKER.test(unmarked) || /\bAction\s+Status\b/i.test(unmarked);
  const firstSentence = unmarked.split(/(?<=\.)\s+/)[0] ?? '';
  if (!hasEntryStructure && SECTION_PROSE.some((r) => r.test(firstSentence))) {
    return { actions: [], narrative: unmarked };
  }

  // Ordered most-structured first; the first parser that yields anything wins.
  for (const attempt of [parseCheckmark, parseKeyValue, parseEntityStatus, parseNarrative]) {
    const parsed = attempt(body).filter(isRenderable);
    if (parsed.length > 0) return { actions: dedupe(parsed), narrative: null };
  }

  return { actions: [], narrative: unmarked };
}

/**
 * Remove the section's own prose, keeping whatever describes actions.
 *
 * Sentence-wise rather than whole-blob, because production tickets put the
 * lead-in, the use-case pointer and the actions in one unbroken run of text.
 */
function stripProse(text: string): string {
  const sentences = text.split(/(?<=\.)\s+/).map((s) => s.trim()).filter(Boolean);
  const kept = sentences.filter((s) => !SECTION_PROSE.some((p) => p.test(s)));
  return kept.join(' ').replace(ENTRY_MARKERS, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * A row worth rendering.
 *
 * Requires a NAMED action — not merely a recognised status. A status alone
 * produced rows like "Action Status customersuccess@CyFlare.com , SOAR…", a
 * truncated sentence presented as an action with a button to request it.
 */
function isRenderable(action: SocAction): boolean {
  return action.matchedLabel && !SECTION_PROSE.some((p) => p.test(action.rawText));
}

/**
 * Collapse entries that name the same action for the same entity.
 *
 * The entity+status format repeats the entity per line, and a ticket listing
 * two failed attempts at one action should read as one action, not two rows the
 * user might request twice.
 */
function dedupe(actions: SocAction[]): SocAction[] {
  const seen = new Set<string>();
  return actions.filter((a) => {
    const key = `${a.label}|${a.hostname ?? ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// ---------------------------------------------------------------------------
// Format parsers
// ---------------------------------------------------------------------------

/**
 * Marker-delimited entries.
 *
 * The corpus uses four markers, not one — measured over 97,024 sections:
 *   ⚠  31,528   the action did not run (Not Configured / Skipped)
 *   (?) 9,981   outcome unrecorded
 *   ✔   5,084   the action succeeded
 *   ❌    778   the action failed
 *
 * Only ✔ was handled, so roughly a third of all sections — every ⚠ ticket —
 * missed this parser entirely and fell through to the narrative fallback,
 * which is what rendered them as a raw blob.
 *
 * The marker is NOT trusted for status: entries carry their own status word and
 * that is what is read. The marker only tells us where an entry begins.
 */
function parseCheckmark(text: string): SocAction[] {
  if (!MARKER.test(text)) return [];
  MARKER.lastIndex = 0;
  const pattern = /[✔✓✅☑⚠❌]\s*"?([^",]*)"?\s*,\s*(.+?)(?=\s*[✔✓✅☑⚠❌]|$)/g;
  const out: SocAction[] = [];
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    const entity = match[1].trim();
    out.push(build(match[2].trim(), entity || null));
  }
  return out;
}

/** Isolate Endpoint: N/A Initiate Full Disk Scan: N/A */
function parseKeyValue(text: string): SocAction[] {
  const KEYS = [
    'Initiate Full Disk Scan', 'Full Disk Scan', 'Isolate Endpoint', 'Network Isolation',
    'Block IP', 'Block Domain', 'Quarantine Email', 'Quarantine User', 'Disable User',
    'Force Password Reset', 'Revoke Sessions', 'Remove Inbox Rule',
  ];
  const hits: { key: string; keyStart: number; keyEnd: number; valueStart: number }[] = [];

  for (const key of KEYS) {
    const pattern = new RegExp(`${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:\\s*`, 'gi');
    let m: RegExpExecArray | null;
    while ((m = pattern.exec(text)) !== null) {
      hits.push({ key, keyStart: m.index, keyEnd: m.index + key.length, valueStart: m.index + m[0].length });
    }
  }
  if (hits.length === 0) return [];

  // Longest match wins at equal starts, then drop keys nested inside another —
  // "Full Disk Scan" also matches inside "Initiate Full Disk Scan", which used
  // to emit a phantom third action reading "Initiate Full Disk Scan: Full Disk
  // Scan:" with no status.
  hits.sort((a, b) => a.keyStart - b.keyStart || b.keyEnd - a.keyEnd);
  const keys = hits.filter((h, i) => i === 0 || h.keyStart >= hits[i - 1].keyEnd);

  return keys.map((hit, i) => {
    const valueEnd = i + 1 < keys.length ? keys[i + 1].keyStart : text.length;
    const value = text.substring(hit.valueStart, valueEnd).trim();
    return build(hit.key, null, value);
  });
}

/**
 * Entity + status, split on the STATUS tokens.
 *
 * "dkoech@cyflare.com , revoke user's active sessions Not Configured
 *  dkoech@cyflare.com , disable user account Not Configured"
 *
 * Splitting on status rather than on the entity is what makes this work for
 * both hostnames and emails: the old parser required `[A-Z][A-Z0-9-]{3,}`, so
 * every email-based ticket fell through to narrative and rendered as a blob.
 */
function parseEntityStatus(text: string): SocAction[] {
  STATUS_RE.lastIndex = 0;
  const statuses: { token: string; start: number; end: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = STATUS_RE.exec(text)) !== null) {
    statuses.push({ token: m[0], start: m.index, end: m.index + m[0].length });
  }
  if (statuses.length === 0) return [];

  const out: SocAction[] = [];
  let cursor = 0;
  for (const s of statuses) {
    const segment = text.substring(cursor, s.start).trim();
    cursor = s.end;
    if (!segment) continue;

    const entityMatch = ENTITY_RE.exec(segment);
    const entity = entityMatch ? entityMatch[1] : null;
    const description = entityMatch ? segment.substring(entityMatch[0].length).trim() : segment;
    if (!description) continue;

    out.push(build(description, entity, s.token));
  }
  return out;
}

/** Endpoint isolated via SentinelOne network quarantine. File quarantined. */
function parseNarrative(text: string): SocAction[] {
  return text
    .split(/(?<=\.)\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 5)
    .map((sentence) => build(sentence, null));
}

// ---------------------------------------------------------------------------
// Construction
// ---------------------------------------------------------------------------

/**
 * Build one action.
 *
 * `label` is only ever a RECOGNISED action name. When nothing matches, the row
 * is marked unrenderable rather than given a truncated version of its own text —
 * the label is what the user reads and what the Request SOC button acts on.
 */
function build(description: string, entity: string | null, statusSource?: string): SocAction {
  const known = matchActionLabel(description);
  const { status, statusText } = statusSource
    ? classifyStatus(statusSource)
    : inferStatusFromText(description);

  return {
    label: known ?? description,
    matchedLabel: !!known,
    status,
    statusText,
    hostname: entity,
    rawText: description,
  };
}

function matchActionLabel(text: string): string | null {
  for (const { pattern, label } of ACTION_LABELS) {
    if (pattern.test(text)) return label;
  }
  return null;
}

/**
 * Status when it is not a delimiter — i.e. narrative and checkmark entries,
 * where it is embedded in the sentence.
 */
function inferStatusFromText(text: string): { status: SocAction['status']; statusText: string } {
  STATUS_RE.lastIndex = 0;
  const found = STATUS_RE.exec(text);
  if (found) return classifyStatus(found[0]);

  // A narrative sentence in the past tense IS the report that it happened.
  if (/\b(?:isolated|quarantined|blocked|removed|disabled|revoked|remediated|reset|sent|executed)\b/i.test(text)) {
    return { status: 'taken', statusText: 'Completed' };
  }
  if (/\bno\b.*\b(?:command|action)\b|was not\b|were not\b/i.test(text)) {
    return { status: 'not_taken', statusText: 'Not Taken' };
  }
  return { status: 'unknown', statusText: 'Unknown' };
}
